import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Icon from 'react-web-vector-icons';
import { utils, writeFileXLSX } from 'xlsx';
import downloadExcelIcon from '../../assets/buttons/excel.svg';
import saveDiskIcon from '../../assets/buttons/save-one.svg';
import Button from "../../components/Button";
import CopyJsonToClipboard from "../../components/CopyJsonToClipboard";
import FilterIcon from '../../components/FilterIcon';
import Dropdown from "../../components/FormFields/Dropdown";
import ListManager from '../../components/ListManager.js';
import LoadingPanel from "../../components/LoadingPanel";
import PageBase from "../../components/PageBase";
import PopupWin from "../../components/PopupWin";
import PopupButton from "../../components/PopupButton";
import Row from "../../components/Row";
import Toaster from "../../components/Toaster";
import useColumnFilters from '../../hooks/useColumnFilters';
import * as campaignActions from "../../redux/Campaign/actions";
import * as userActions from '../../redux/User/actions';
import ContextProvider from "../../utils/ContextApi";
import { columnsWidths, returnParsedColumnName } from "../../utils/columns";
import { createColumnConfigs } from '../../utils/fieldUtils.js';
import { getDisplayFields } from '../../utils/fields/accounts.js';
import { getFilterFunction } from '../../utils/filterFunctions';
import { capitalize, isNullOrEmptyArray } from "../../utils/general";
import { ext } from "../../utils/general.js";
import * as ROUTES from "../../utils/routes";
import { isAdmin } from "../../utils/user.js";
import AccountManagerReportPopup from "./AccountManagerReportPopup";
import './Mapping.scss';
import Reports from "./Reports";
import SaveListModal from "./SaveListModal";
import SavedListsTable from "./SavedListsTable";
import SelectAccount from "./SelectAccount";
import SelectCampaign from "./SelectCampaign";
import SetupRow from "./SetupRow";
import FloatingAddToCampaignBar from "./FloatingAddToCampaignBar";

const similarFilterEnum = {
	ALL: 'ALL',
	SIMILAR: 'SIMILAR',
	NON_SIMILAR: 'NON_SIMILAR',
};

const searchAccountNameColumn = 'original_name';

const Mapping = props => {
	const { accountManagerReportData, setAccountManagerReportData, setHeader } = useContext(ContextProvider);
	const [filters, setFilters] = useState([]);
	const [loading, setLoading] = useState(false);
	const [similarFilter, setSimilarFilter] = useState(similarFilterEnum.ALL);
	const [searchMethod, setSearchMethod] = useState('manual');
	const [showSaveListModal, setShowSaveListModal] = useState(false);
	const [showSavedLists, setShowSavedLists] = useState(false);
	const [selectedCampaignId, setSelectedCampaignId] = useState('-1');
	const [fileName, setFileName] = useState(null);
	const [hideOriginalName, setHideOriginalName] = useState(false);
	const [editingName, setEditingName] = useState(null);
	const [showPickCampaign, setShowPickCampaign] = useState(false);
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [selectedContactRelatedAccount, setSelectedContactRelatedAccount] = useState([]);
	const [excludedRows, setExcludedRows] = useState([]);
	const [lastSelectedRowIndex, setLastSelectedRowIndex] = useState(null);
	const [selectAll, setSelectAll] = useState(false);
	const [loadedListId, setLoadedListId] = useState(null);
	const [loadedListName, setLoadedListName] = useState(null);
	const [selectedReportLogic, setSelectedReportLogic] = useState('AND');
	const [selectedVendor, setSelectedVendor] = useState('aws');
	const [selectedRegions, setSelectedRegions] = useState([]);
	const [selectedSegments, setSelectedSegments] = useState([]);
	const [savedMatch, setSavedMatch] = useState([])
	const [reportMethodInitialized, setReportMethodInitialized] = useState(false)
	const navigate = useNavigate();
	const context = searchMethod=='report' ? 'report' : 'list';
	const tableRowRefs = React.useRef({});
	const [floatingBarPosition, setFloatingBarPosition] = React.useState(null);

	const user = useSelector(state => state.user.user);
	const [data, setData] = useState(null);
	const [headers, setHeaders] = useState([]);
	const [requested, setRequested] = useState(0);
	const dispatch = useDispatch();
	const hiddenFileInput = React.useRef(null);
	const [listColumns, setListColumns] = useState([]);
	const EXCLUDED_FIELDS = ['contact_id', 'account_id'];
	const fieldsToUse = useMemo(() => {
  		return createColumnConfigs(getDisplayFields(selectedVendor, listColumns.length === 0,context,listColumns),).map(f => f.id);
	}, [listColumns, selectedVendor]);
	const defaultFields = useMemo(
		() => getDisplayFields(selectedVendor,true).map(f => f.field),
		[selectedVendor]
	);

	const visibleHeaders = headers
  	.filter(h => !EXCLUDED_FIELDS.includes(h));

	// const fieldsToUse = (listColumns.length > 0 ? listColumns : visibleHeaders)
	// .filter(h => h !== 'contact_id' && h !== 'account_id');
	const fieldsForListManager = getDisplayFields(selectedVendor,false,context)
	// const defaultFields = visibleHeaders;

	// Helper function to get field label from accounts.js configuration
	const getFieldLabel = (fieldKey) => {
		const field = fieldsForListManager.find(f => f.field === fieldKey);
		return field ? field.label : returnParsedColumnName(fieldKey);
	};

	// Memoize the columns configuration
	const columnsConfig = useMemo(() => 
		fieldsToUse.map(header => ({ id: header })),
		[listColumns]
	);

	// Memoize the data to prevent unnecessary updates
	const memoizedData = useMemo(() => data || [], [data]);

	// Get storage key based on search method
	const getStorageKey = () => {
		if (searchMethod === 'report') {
			return `mapping_report_${selectedVendor}`;
		} else if (searchMethod === 'manual') {
			return `mapping_manual_${selectedVendor}`;
		} else if (searchMethod === 'file') {
			return `mapping_file_${selectedVendor}`;
		}
		return `mapping_${selectedVendor}`;
	};

	// Add useColumnFilters hook with memoized values
	const {
		filters: columnFilters,
		sortBy,
		sortOrder,
		filteredData: filteredDataWithColumnFilters,
		handleFilterChange,
		filterOrder,
		resetWith,
		updateData,
		uniqueValuesByColumn
	} = useColumnFilters({
		data: data || [],
		columns: columnsConfig,
		storageKey: getStorageKey(),
		initialState: {}
	});

	const listManagerComponent = useMemo(() => {
		return (
			<ListManager
				entityType={`${selectedVendor}_accounts`}
				displayFields={fieldsForListManager}
				defaultFields={defaultFields}
				onColumnsChange={setListColumns}
			/>
		);
	}, [data, headers, selectedVendor, fieldsForListManager, defaultFields]);

	const selectCampaignFooter = useMemo(() => (
		<>
			<PopupButton
				variant="secondary"
				onClick={() => closePopup()}
			>
				Cancel
			</PopupButton>
			<PopupButton
				variant="primary"
				disabled={selectedCampaignId == null || selectedCampaignId === '-1'}
				onClick={() => {
					dispatch(campaignActions.addCampaignContact(selectedCampaignId, selectedContacts, (result) => {
						if(result.status === "ok") {
							Toaster.showSuccess("Added to campaign!");
						}else{
							Toaster.showError(result.message);
						}
						setSelectedCampaignId('-1');
						setSelectedContacts([]);
						setShowPickCampaign(false);
					}));
				}}
			>
				Select Campaign
			</PopupButton>
		</>
	), [selectedCampaignId, selectedContacts, dispatch]);

	// Memoized footer for Account Name popup
	const accountNameFooter = useMemo(() => (
		<>
			<PopupButton variant="secondary" onClick={() => closePopup()}>Cancel</PopupButton>
		</>
	), []);

	// Update data in the hook when it changes
	useEffect(() => {
		if (data) {
			updateData(data);
		}
	}, [data]);

	// Calculate floating bar position based on last selected row
	useEffect(() => {
		if (lastSelectedRowIndex !== null && tableRowRefs.current[lastSelectedRowIndex]) {
			const rowElement = tableRowRefs.current[lastSelectedRowIndex];
			const rect = rowElement.getBoundingClientRect();
			const tableWrapperSelector = searchMethod === 'manual' ? '.mapping-results' : '.mapping-table';
			const tableWrapper = document.querySelector(tableWrapperSelector);
			const tableRect = tableWrapper?.getBoundingClientRect();

			if (tableRect) {
				setFloatingBarPosition({
					top: rect.bottom - tableRect.top - 5, // 10px below the row
					left: 178 // Align to right side of row
				});
			}
		} else {
			setFloatingBarPosition(null);
		}
	}, [lastSelectedRowIndex, selectedContacts, searchMethod]);

	// Reset filters when search method changes

	// Memoize the filter function to prevent recreation on every render
	const filterDataWithIcons = useMemo(() => {
		if(isNullOrEmptyArray(memoizedData)) return [];
		let result = memoizedData;
		const keys = Object.keys(memoizedData[0] || {});
		
		// Apply text input filters
		keys.forEach(key => {
			if (filters[key] && filters[key].length > 0) {
				result = result.filter(r => {					
					if (r[key] === undefined || r[key] === null) return false;
					return r[key].toString().toLowerCase().indexOf(filters[key].toString().toLowerCase()) > -1;
				});
			}
		});

		// Apply FilterIcon filters
		keys.forEach(key => {
			const filter = columnFilters[key];
			if (!filter) return;
			// Text filter
			if (filter.textFilter && filter.textFilter.type && filter.textFilter.value) {
				const fn = getFilterFunction('text', filter.textFilter.type);
				if (fn) {
					result = result.filter(row => fn((row[key] || '').toString(), filter.textFilter.value));
				}
			}
			// Checkbox filter
			if (filter.checkboxFilters && filter.checkboxFilters.length > 0) {
				result = result.filter(row => {
					const cellValue = row[key];
					const normalized = cellValue === '' || cellValue == null ? '(None)' : cellValue;
					return filter.checkboxFilters.includes(normalized);
				});
			}
		});

		// Apply similar filter
		if(similarFilter === similarFilterEnum.SIMILAR) {
			result = result.filter(r => r.match_type === 'similarity');
		}
		else if(similarFilter === similarFilterEnum.NON_SIMILAR) {
			result = result.filter(r => r.match_type !== 'similarity');
		}

		// Sorting
		if (sortBy && sortOrder) {
			result = [...result].sort((a, b) => {
				const aVal = a[sortBy] || '';
				const bVal = b[sortBy] || '';
				if (sortOrder === 'asc') {
					return aVal.toString().localeCompare(bVal.toString());
				} else {
					return bVal.toString().localeCompare(aVal.toString());
				}
			});
		}
		return result;
	}, [memoizedData, filters, columnFilters, similarFilter, sortBy, sortOrder]);

	// Use the memoized filtered data
	const filteredDataWithIcons = filterDataWithIcons;

	// Auto-update selectAll checkbox when all items are selected
	useEffect(() => {
		if (filteredDataWithIcons && filteredDataWithIcons.length > 0) {
			const selectableRows = filteredDataWithIcons.filter(row => row.contact_id != null && row.contact_id !== '');
			const allSelected = selectableRows.length > 0 && selectableRows.every(row =>
				selectedContacts.some(contact =>
					contact.contact_id === row.contact_id && contact.account_id === row.account_id
				)
			);
			setSelectAll(allSelected);
		}
	}, [selectedContacts, filteredDataWithIcons]);

	if(ext(user)) {
		return <div/>;
	}

	const userIsAdmin = isAdmin();

	const similarFilterTypes = [
		{ key: similarFilterEnum.ALL, label: 'All Results' },
		{ key: similarFilterEnum.SIMILAR, label: 'Only Similar' },
		{ key: similarFilterEnum.NON_SIMILAR, label: 'Only Non-Similar' }
	];

	const updateSimilarFilter = (value) => {
		setSimilarFilter(value);
	};

	const addToCampaignColumns =  ['contact_name'];
	const linkColumns =  [searchAccountNameColumn];
	const amColumns = ['AccountManagerAlias'];

	const toggleExcludedRows = account_id => {
		let updated;
		if(excludedRows.find(i => i === account_id) > -1) {
			updated = excludedRows.filter(f => f !== account_id);
		}
		else {
			updated = [...excludedRows, account_id];
		}
		setExcludedRows(updated);

		if(loadedListId) {
			dispatch(userActions.updateSavedListExcludedRows(loadedListId, updated));
		}
	};

	const copyData = () => {
		let toDownload = filteredDataWithIcons.filter(d => excludedRows.indexOf(d.original_name) < 0);

		toDownload = toDownload.map(row => {
			Object.values(row).map((value, i) => {
				const header = Object.keys(row)[i];
				if(header === 'account_name' || header === "original_name" || header?.endsWith("Name")) {
					row[header] = capitalize(value);
				}
			});
			return row;
		});

		if(isNullOrEmptyArray(toDownload)) {
			Toaster.showWarn("Cannot copy an empty list.");
			return;
		}

		CopyJsonToClipboard(toDownload);
		Toaster.showSuccess(`Copied!`);
	};

	const doSearch = async (accounts, regions, segments) => {
		const max = 500;

		if(accounts.length > max) {
			Toaster.showWarn(`Please upload up to ${max} accounts. (your entered ${accounts.length}).`);
			return;
		}

		resetView();
		setLoading(true);
		await dispatch(userActions.searchAccounts(selectedVendor, accounts.map(str => str.trim()), regions, segments, searchLoaded));
	};

	const searchLoaded = result => {
		setLoading(false);
		if(result === null) {
			Toaster.showWarn("error loading accounts. Please try again.");
			return;
		}
		if(result){
			if(result.status === "timeout") {
				Toaster.showWarn("Your request was too heavy and timed out. Please try with fewer accounts.");
				return;
			}
			
			if(!isNullOrEmptyArray(result.data)) {
				
					// 14/11/23 - decided to remove this check because is it confusing that sometime account is link and somethings same account it isn't
					// const originalNamesAndAccountNamesAreSame = result.data.every((obj, i, arr) => obj.original_name === obj.account_name);
				// setHideOriginalName(originalNamesAndAccountNamesAreSame);
				
				const headers = Object.keys(result.data[0]);
				setHeaders(headers);
				setData(result.data);
				setRequested(result.requested);
				
				let filters = {};
				headers.forEach(column => {
					filters[column] = '';
				});
				setFilters(filters);
			}
		}
		else {
			Toaster.showWarn("No results");
		}
	};


	const getColumnWidth = caption => {
		return '300px';
	};

	const resetView = () => {
		setData(null);
		setHeaders([]);
		setRequested(0);
		setEditingName(null);
		setExcludedRows([]);
		setLoadedListId(null);
		setLoadedListName(null);
		setShowSavedLists(false);
		// Reset checkbox and floating bar states
		setSelectedContacts([]);
		setSelectAll(false);
		setLastSelectedRowIndex(null);
		setFloatingBarPosition(null);
	};

	const generateAMReport = (accountManager) => {
		console.log("generateAMReport - Enter");
		setLoading(true);
		const field = 'contact_email'; //selectedVendor === 'aws' ? 'account_manager' : 'a.AccountManagerAlias';
		const value = accountManager; //selectedVendor === 'aws' ? accountManager : accountManager.split('@')[0];
		const rows =[[{key: 0, field, type: "exactly", value}]];
		dispatch(userActions.runReport(selectedVendor, rows, data => accountManagerReportLoaded(data, accountManager)));
	};

	const accountManagerReportLoaded = (result, accountManager) => {
		setLoading(false);
		setHeader({name:"Account Manager", value: accountManager, tableName: "Accounts"});
		setAccountManagerReportData(result.data);
	};

	const onAddToCampaign = (row) => {
		// setSelectedContactRelatedAccount({id: row.account_id, name: row.account_name});
		// setSelectedContactIds(row.contact_id);
		setShowPickCampaign(true);
	}

	const handleSelectAll = (e) => {
		const checked = e.target.checked;
		setSelectAll(checked);

		if (checked) {
			// Select all filtered contacts that have contact_id
			const allContacts = filteredDataWithIcons
				.filter(row => row.contact_id != null && row.contact_id !== '')
				.map(row => ({
					contact_id: row.contact_id,
					account_id: row.account_id,
					account_name: row.account_name
				}));
			setSelectedContacts(allContacts);
			// Set last selected to last row
			setLastSelectedRowIndex(filteredDataWithIcons.length - 1);
		} else {
			setSelectedContacts([]);
			setLastSelectedRowIndex(null);
		}
	};

	const renderContactName = (value, header, rowData) => {
		const onClick = header === 'contact_name' && (searchMethod === 'manual' || searchMethod === 'report') ? () => onContactClick(rowData) : () => generateAMReport(rowData?.contact_email);

		return (
			<span style={{textDecoration: 'none'}} title="Managed Accounts" className="mapping-table-links-column" onClick={onClick}>
				{value}
			</span>
		);
	};

	const createLinks = value => {
		return (
			<span className="mapping-table-links-column" onClick={() => setEditingName(value)}>
				{value}
			</span>
		);
	};

	const closePopup = () => {
		setEditingName(null);
		setShowPickCampaign(false);
		setSelectedCampaignId('-1');
	};

	const saveMatch = row => {
		const index = data.findIndex(d => d.original_name.toLowerCase() === editingName.toLowerCase());

		try {
			Object.keys(data[index]).forEach(column => {
				if(column !== searchAccountNameColumn) {
					data[index][column] = row[column];
				}
				data[index].match_type = "saved-match"
			});
		}
		catch(e) {
			console.log(index);
			console.log(row);
			console.log("Error: ", e);
		}

		console.log("save connection: ", data[index].original_name, row.account_name);
		const key = data[index]?.original_name
		const value = row.account_name		
		console.log("saved match: ", savedMatch);

		setSavedMatch((prevList) => {
			
			const index = prevList.findIndex((item) => item.key === key);
			if (index !== -1) {
			  // Key exists, update the value
			  const updatedList = [...prevList];
			  updatedList[index].value = value;
			  updatedList[index].account_id = row.account_id;
			  return updatedList;
			} else {
			  // Key doesn't exist, add the new item
			  return [...prevList, { key, value, account_id: row.account_id }];
			}
		  });
		dispatch(userActions.saveUserMatch(data[index].original_name, row.account_name, selectedVendor));

		if(loadedListId) {
			const accounts = data.map(d => ({original_name: d.original_name, account_name: d.account_name}));
			dispatch(userActions.updateSavedListAccounts(loadedListId, accounts));
		}

		setEditingName(null);
	};

	const unsaveMatch = row => {
		const index = data.findIndex(d => d.original_name.toLowerCase() === editingName.toLowerCase());

		try {
			Object.keys(data[index]).forEach(column => {
				if(column !== searchAccountNameColumn) {
					data[index][column] = row[column];
				}
				data[index].match_type = "saved-match"
			});
		}
		catch(e) {
			console.log(index);
			console.log(row);
			console.log("Error: ", e);
		}

		console.log("save connection: ", data[index].original_name, row.account_name);
		dispatch(userActions.unsaveUserMatch(data[index].original_name, row.account_name, selectedVendor));
		setSavedMatch(p => p.filter(e => e.key !== data[index].original_name ))

		if(loadedListId) {
			const accounts = data.map(d => ({original_name: d.original_name, account_name: d.account_name}));
			dispatch(userActions.updateSavedListAccounts(loadedListId, accounts));
		}
	};

	const onListSaved = () => {
		setShowSaveListModal(false);
		Toaster.showSuccess(`List saved!`);
	};

	function listLoaded(result) {
		setShowSavedLists(false);
		setSelectedVendor(result.vendor);
		setExcludedRows(result.excluded_accounts);
		setLoadedListId(result.listId);
		setLoadedListName(result.listName);
		searchLoaded(result);
	}

	const onRunReport = (queryRows) => {
		setLoading(true);
		setData(null);
		dispatch(userActions.runReport(selectedVendor, queryRows, searchLoaded));
		setReportMethodInitialized(false)
	};

	const updateFilter = (value, column) => {
		const f = {...filters};
		f[column] = value;
		setFilters(f);
	};

	const filterField = (key, i) => {
		return (
			<td key={i} style={{width: getColumnWidth(key), display: getColumnWidth(key) === '0px' ? 'none' : ''}}>
				<input type="text" value={filters[key] || ""} onChange={e => updateFilter(e.target.value, key)} style={{width: getColumnWidth(key), outline: 'none', padding: '5px', border: '1px solid #ccc', borderRadius: '4px'}} />
			</td>
		)
	};

	const onSetupAction = result => {
		// Only hide components when not switching to their respective modes
		if(result.type !== 'list') {
			setShowSavedLists(false);
		}
		setFileName(null);
		if(savedMatch.length){
			setSavedMatch([])
		}

		switch(result.type) {
			case 'reset':
				resetView();
				if(result.vendor) {
					setSelectedVendor(result.vendor);
				}
				break;
			case 'manual':
				console.log("onSetupAction - manual", result);
				doSearch(result.accounts, result.regions, result.segments);
				setSelectedRegions(result.regions);
				setSelectedSegments(result.segments);
				break;
			case 'file':
				setFileName(result.fileName);
				doSearch(result.accounts, result.regions, result.segments);
				setSelectedRegions(result.regions);
				setSelectedSegments(result.segments);
				break;
			case 'list':
				if(loadedListId == null) {
					setShowSavedLists(true);
				}
				break;
			case 'report':
				setSelectedReportLogic(result.logic);
				break;
		}
	};

	const filterData = () => {
		if(isNullOrEmptyArray(data)) {
			return [];
		}

		let result = data;
		const keys = Object.keys(data[0]);
		keys.forEach(key => {
			
			if (filters[key] && filters[key].length > 0) {
				result = result.filter(r => {					
					if (r[key] === undefined || r[key] === null) return false; // Skip missing values
					
					return r[key].toString().toLowerCase().indexOf(filters[key].toString().toLowerCase()) > -1;
				});
			}
		});

		if(similarFilter === similarFilterEnum.SIMILAR) {
			result = result.filter(r => r.match_type === 'similarity');
		}
		else if(similarFilter === similarFilterEnum.NON_SIMILAR) {
			result = result.filter(r => r.match_type !== 'similarity');
		}

		return result;
	};

	const filteredData = filterData(data);

	const onClickSave = () => {
		if(!isNullOrEmptyArray(filteredData)) {
			setShowSaveListModal(true);
		}
		else {
			Toaster.showWarn("Cannot save an empty list.");
		}
	};

	const exportFile = () => {
		let toDownload = filteredDataWithIcons.filter(d => excludedRows.indexOf(d.original_name) < 0);

		toDownload = toDownload.map(row => {
			Object.values(row).map((value, i) => {
				const header = Object.keys(row)[i];
				if(header === 'account_name' || header === "original_name" || header?.endsWith("Name")) {
					row[header] = capitalize(value);
				}
			});
			return row;
		});

		if(isNullOrEmptyArray(toDownload)) {
			Toaster.showWarn("Cannot export an empty list.");
			return;
		}

		toDownload = toDownload.map((obj) => {
			const newObj = {};
			for (const key in obj) {
				const newKey = getFieldLabel(key);
				newObj[newKey] = obj[key];
			}
			return newObj;
		});

		dispatch(userActions.requestDownload(toDownload.length, (result) => {
			if(result.status === "ok") {
				const ws = utils.json_to_sheet(toDownload);
				const wb = utils.book_new();

				utils.book_append_sheet(wb, ws, "Data");
				writeFileXLSX(wb, "file.xlsx");
			}
			else {
				Toaster.showWarn(result.message);
			}
		}));
	};

	const onSelectContactCampaign = (campaign) => {
		// const campaignIds = campaign.map(c => c.value);
		setSelectedCampaignId(campaign.value);
	};

	const onChangeSearchMethod = (method) => {
		setSearchMethod(method)
		if(method === 'report'){
			setReportMethodInitialized(true)
		}
	}

	const onContactClick = async (row) => {
			const report ={field: `contact_name`, value: row.contact_name, relatedTable: 'accounts'};

			navigate(`/contact/${selectedVendor}/${row.contact_id}`, { 
					state: { 
							row, 
							title: "Contact",
							headerLinkTexts:"All contacts", 
							headerLinkTarget: ROUTES.CONTACTS, 
							titleName: row.contact_name,
							report
					} 
			});
		};

	return (
		<PageBase title="Account Mapping" removeMargins>

			{
				accountManagerReportData != null &&
				<AccountManagerReportPopup />
			}

			{
				showSaveListModal &&
				<SaveListModal accountNames={filteredDataWithIcons.map(d => ({original_name:searchMethod === 'report'? d.account_name:d.original_name, account_name: d.account_name}))}
				               excludedAccounts={excludedRows}
				               onListSaved={onListSaved}
				               vendor={selectedVendor}
				               fileName={fileName}
				               onClose={() => setShowSaveListModal(false)}
				/>
			}

			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={showPickCampaign}
				onClose={() => closePopup()}
				title="Select Campaign"
				contentStyle={{width: '100%', height: '90%'}}
				popupStyle={{width: '700px', height: '500px', overflow: 'none'}}
				footer={selectCampaignFooter}>
				<div style={{display: 'flex', flexDirection: 'column', padding: '20px'}}>
					<div style={{height: '320px'}}>
						<SelectCampaign selectedVendor={selectedVendor} onSelectContactCampaign={onSelectContactCampaign} />
					</div>
				</div>
			</PopupWin>

			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={editingName != null}
				onClose={() => closePopup()}
				title="Account Name:"
				subtitle={editingName}
				contentStyle={{width: '100%', height: '90%'}}
				popupStyle={{width: '90%', maxWidth: '1600px', height: '80%', maxHeight: '800px', overflow: 'none'}}
				footer={accountNameFooter}>
				<SelectAccount selectedVendor={selectedVendor} regions={selectedRegions} segments={selectedSegments} value={editingName} saveMatch={saveMatch} unsaveMatch={unsaveMatch} savedMatch={savedMatch} />
			</PopupWin>

			<div className="mapping-container">

				{ loading && <LoadingPanel /> }

				<SetupRow
					onAction={onSetupAction}
					listName={loadedListName}
					searchMethod={searchMethod}
					onChangeSearchMethod={onChangeSearchMethod}
					listManager={listManagerComponent}
				/>

				{ showSavedLists && <SavedListsTable onListLoaded={listLoaded} /> }

			{ searchMethod === 'report' && <Reports vendor={selectedVendor} logic={selectedReportLogic} runReport={onRunReport} /> }

				{(searchMethod !== 'report' || data != null) && (
					<div className="mapping-results">

						{ data != null && <Row style={{justifyContent: 'space-between'}}>
							<div className="mapping-search-title">
								{
									data.length === 0 ? <span>Found 0 results</span> : filteredDataWithIcons.length === data.length ?
										<span>Found {data.length} results</span>
										:
										<span>Found {filteredDataWithIcons.length} results:</span>
								}
							</div>
							{/* {data && headers.length > 0 && ( */}
							
							{/* )} */}

							{data.length > 0 && headers.length&& <Row style={{columnGap: '15px', marginRight: '15px'}}>
								{/* <Dropdown items={similarFilterTypes} onChange={updateSimilarFilter} selectedValue={similarFilter} keyField="key" labelField="label" /> */}
								<Button type="text" width={80} leftIcon={saveDiskIcon} onClick={onClickSave}>Save</Button>
								<Button type="text" width={150} leftIcon={downloadExcelIcon} onClick={exportFile}>Download Excel</Button>
								{ userIsAdmin && <Button type="text" width={115} leftFontIcon={<Icon name="content-copy" font="MaterialCommunityIcons" color="#40A0C1" size={17}/>} onClick={copyData}>Copy Data</Button> }
							</Row>}
						</Row>}
						<div className="mapping-table-wrapper">
							{/* Floating Add to Campaign Bar - for manual mode */}
							{searchMethod === 'manual' && (
								<FloatingAddToCampaignBar
									count={selectedContacts.length}
									onAddToCampaign={onAddToCampaign}
									position={floatingBarPosition}
								/>
							)}
							<table className="mapping-table">
								{/* Floating Add to Campaign Bar - for report mode */}
								{searchMethod === 'report' && (
									<FloatingAddToCampaignBar
										count={selectedContacts.length}
										onAddToCampaign={onAddToCampaign}
										position={floatingBarPosition}
									/>
								)}
								<thead>
								<tr style={{display: 'flex', border: 'none'}}>
									<th key={-1} className="mapping-table-header-cell"
										style={{width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
										{data != null && <input
											type="checkbox"
											className="checkbox-primary"
											checked={selectAll}
											onChange={handleSelectAll}
											title="Select All"
										/>}
									</th>
									{
										fieldsToUse
											.filter(h =>  (h !== 'match_type' || userIsAdmin) && h !== (hideOriginalName ? searchAccountNameColumn : '-999'))
											.map((key) => {
												return (
													<th key={key} className="mapping-table-header-cell" style={{
														width: getColumnWidth(key),
														backgroundColor: 'transparent'
													}}>
														<span style={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center'
														}}>
															<span>{getFieldLabel(key)}</span>
															<span>
																<FilterIcon
																	column={key}
																	data={memoizedData}
																	onFilterChange={handleFilterChange}
																	allUniqueValues={uniqueValuesByColumn[key] || []}
																	filters={columnFilters}
																	sortBy={sortBy}
																	sortOrder={sortOrder}
																	filterOrderIndex={filterOrder?.indexOf(key)}
																/>
															</span>
														</span>
													</th>
												);
											})
									}
								</tr>
								</thead>
								<tbody>
									<tr style={{display: 'flex', backgroundColor: '#FFFFFF'}}>
										<td key={-1} style={{width: '40px'}}>&nbsp;</td>
										{
											fieldsToUse
												.filter(h =>(h !== 'match_type' || userIsAdmin) && h !== (hideOriginalName ? searchAccountNameColumn : '-999'))
												.map((key, i) => filterField(key, i))
										}
									</tr>
									{ filteredDataWithIcons.map((row, index) => {
										const excluded = excludedRows.indexOf(row.account_id) > -1;
			
										const previousRow = index > 0 ? filteredDataWithIcons[index - 1] : null;
										const values = Object.values(row)
										const previousValues = previousRow ? Object.values(previousRow)
										: null;
										
										const shouldHideFirstColumns = previousValues && 
											values[0] === previousValues[0] && 
											values[1] === previousValues[1];

										const isSelected = selectedContacts.some(contact => contact.contact_id === row.contact_id && contact.account_id === row.account_id);

										return (
											<tr
												ref={el => tableRowRefs.current[index] = el}
												style={{display: 'flex', backgroundColor: '#FFFFFF', borderTop: shouldHideFirstColumns ? 'none' : '1px solid #DBE7F1'}}
												key={index}
											>
												<td className="mapping-table-body-cell"
													key={`checkbox-${index}`}
													style={{
														width: '40px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														backgroundColor: isSelected ? 'rgba(211, 225, 237, 0.31)' : '#FFFFFF'
													}}>
													{!shouldHideFirstColumns && (
														<input
															type="checkbox"
															className="checkbox-primary"
															checked={selectedContacts.some(contact => contact.contact_id === row.contact_id && contact.account_id === row.account_id)}
															disabled={row.contact_id == null || row.contact_id === ''}
															onChange={() => {
																if (selectedContacts.some(contact => contact.contact_id === row.contact_id && contact.account_id === row.account_id)) {
																	// Deselect
																	setSelectedContacts(selectedContacts.filter((item) => item.contact_id !== row.contact_id && item.account_id !== row.account_id));
																} else {
																	// Select
																	setSelectedContacts([...selectedContacts, { contact_id: row.contact_id, account_id: row.account_id, account_name: row.account_name }]);
																	setLastSelectedRowIndex(index);
																}
															}}
														/>
													)}
												</td>
												{
												 fieldsToUse.map((header, i) => {
														let value  = row[header];
														if(header === 'account_name' || header === "original_name" || header?.endsWith("Name")) {
															value = capitalize(value);
														}
														const isContactNameColumn = addToCampaignColumns.indexOf(header) > -1;
														const linksColumn = !excluded && linkColumns.indexOf(header) > -1;
														const amLink = amColumns.indexOf(header) > -1;
														const similarity = row.match_type === 'similarity';

														const hideContent = shouldHideFirstColumns && (i === 0 || i === 1);

														if (
															(header !== 'match_type' || userIsAdmin) &&
															header !== (hideOriginalName ? searchAccountNameColumn : '-999')) {
															return (
																<td className="mapping-table-body-cell"
																	title={value}
																	key={header}
																	style={{
																		width: getColumnWidth(header),
																		color: excluded ? '#ccc' : 'black',
																		backgroundColor: isSelected ? 'rgba(211, 225, 237, 0.31)' : '#FFFFFF'
																	}}>
																	{ !hideContent && (
																		isContactNameColumn && (header !== 'contact_name' || value) ?
																			renderContactName(value, header, row) :
																		linksColumn ?
																			createLinks(value) :
																		amLink ?
																			<div className="mapping-table-links-column" onClick={() => generateAMReport(row?.contact_email)}>{value}</div> :
																			value
																	)}
																	{ linksColumn && similarity && <div className="mapping-table-similar-tag">Similar!</div> }
																</td>
															)
														}
													})
												}
											</tr>
										)
									})
								}
								</tbody>
							</table>
						</div>
					</div>
				)}

			</div>
		</PageBase>
	)
};

export default Mapping;
